<template>
  <div class="user-details">
    <section class="section">
      <b-loading :active="isLoading"></b-loading>
      <div class="container">
        <div class="columns">
          <div class="column column is-full">
            <div class="box">
              <div class="columns is-mobile" style="margin-top: 16px">
                <div class="column">
                  <header class="heading">
                    <p class="modal-card-title">{{ pageTitle }}</p>
                  </header>
                </div>
                <div v-if="!isEditMode" class="column is-narrow">
                  <b-button class="button" @click="backClicked" tabindex="1" icon-left="arrow-left-bold">
                    {{ $t('common.back') }}
                  </b-button>
                </div>
                <b-dropdown v-if="!isEditMode" class="column is-narrow" :triggers="['hover']"
                            aria-role="list">
                  <template #trigger>
                    <b-button icon-right="menu-down">
                      {{ $t('common.actions') }}
                    </b-button>
                  </template>
                  <b-dropdown-item aria-role="listitem" @click="editClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="pencil"></b-icon>
                      {{ $t('common.edit') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="isPasswordResetVisible" aria-role="listitem" @click="passwordResetClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="lock-reset"></b-icon>
                      {{ $t('password-reset-view.send-password-reset-button') }}
                    </div>
                  </b-dropdown-item>
                  <b-dropdown-item v-if="!isCurrentUserSelected" aria-role="listitem" @click="deleteClicked">
                    <div class="media">
                      <b-icon class="media-left" icon="delete"></b-icon>
                      {{ $t('common.delete') }}
                    </div>
                  </b-dropdown-item>
                </b-dropdown>
                <div v-if="!isLoading && !isUserArchived && !isCurrentUserSelected && isEditMode"
                     class="column is-narrow">
                  <b-button type="is-danger" tabindex="3"
                            icon-left="archive-arrow-down" @click="archiveClicked">
                    {{ $t('user-details-view.archive-user-button') }}
                  </b-button>
                </div>
                <div v-else-if="!isLoading && isUserArchived && !isCurrentUserSelected && isEditMode"
                     class="column is-narrow">
                  <b-button tabindex="4" icon-left="archive-arrow-up-outline"
                            @click="unArchiveClicked">
                    {{ $t('user-details-view.unarchive-user-button') }}
                  </b-button>
                </div>
              </div>
              <div class="columns" style="margin-top: 16px">
                <div class="column is-half">
                  <b-field :label="$t('user-details-view.user-firstname-field')">
                    <b-input v-model="userData.name.first" tabindex="11" v-if="isEditMode"
                             :placeholder="$t('user-details-view.user-firstname-field')"></b-input>
                    <label v-if="!isEditMode">{{ userData.name.first }}</label>
                  </b-field>
                  <b-field :label="$t('user-details-view.user-lastname-field')">
                    <b-input v-model="userData.name.last" tabindex="12" v-if="isEditMode"
                             :placeholder="$t('user-details-view.user-lastname-field')"></b-input>
                    <label v-if="!isEditMode">{{ userData.name.last }}</label>
                  </b-field>
                  <b-field :label="$t('user-details-view.user-role-field')">
                    <b-select
                        v-if="isEditMode && !isCurrentUserSelected && hasCurrentUserAccessToUserRole(userData.roles[0])"
                        v-model="userData.roles[0]" tabindex="13"
                        :placeholder="$t('user-details-view.user-role-field')">
                      <option
                          v-for="option in getUserRoleList()"
                          :value="option.id"
                          :key="option.id">
                        {{ $t(option.translationKey) }}
                      </option>
                    </b-select>
                    <label v-else>
                      {{ userRole }}
                    </label>
                  </b-field>
                  <b-field :label="$t('user-details-view.user-email-field')">
                    <label>{{ userData.email }}</label>
                  </b-field>
                  <b-field :label="$t('user-details-view.user-telephone-field')">
                    <b-input v-model="userData.telephone" tabindex="15" v-if="isEditMode"
                             :placeholder="$t('user-details-view.user-telephone-field')"></b-input>
                    <label v-if="!isEditMode">{{ userData.telephone }}</label>
                  </b-field>
                  <b-field :label="$t('user-details-view.user-mobile-field')">
                    <b-input v-model="userData.mobile" tabindex="16" v-if="isEditMode"
                             :placeholder="$t('user-details-view.user-mobile-field')"></b-input>
                    <label v-if="!isEditMode">{{ userData.mobile }}</label>
                  </b-field>
                </div>
                <div class="column is-half">
                  <div class="columns no-margin">
                    <div class="column is-half">
                      <b-field :label="$t('user-details-view.user-address-street-field')">
                        <b-input v-model="userData.address.street" tabindex="20" v-if="isEditMode"
                                 :placeholder="$t('user-details-view.user-address-street-field')"></b-input>
                        <label v-if="!isEditMode">{{ userData.address.street }}</label>
                      </b-field>
                    </div>
                    <div class="column is-half">
                      <b-field :label="$t('user-details-view.user-address-house-number-field')">
                        <b-input v-model="userData.address.house_number" tabindex="21" v-if="isEditMode"
                                 :placeholder="$t('user-details-view.user-address-house-number-field')"></b-input>
                        <label v-if="!isEditMode">{{ userData.address.house_number }}</label>
                      </b-field>
                    </div>
                  </div>
                  <div class="columns no-margin">
                    <div class="column is-half">
                      <b-field :label="$t('user-details-view.user-address-city-field')">
                        <b-input v-model="userData.address.city" tabindex="22" v-if="isEditMode"
                                 :placeholder="$t('user-details-view.user-address-city-field')"></b-input>
                        <label v-if="!isEditMode">{{ userData.address.city }}</label>
                      </b-field>
                    </div>
                    <div class="column is-half">
                      <b-field :label="$t('user-details-view.user-address-country-field')">
                        <b-input v-model="userData.address.country" tabindex="23" v-if="isEditMode"
                                 :placeholder="$t('user-details-view.user-address-country-field')"></b-input>
                        <label v-if="!isEditMode">{{ userData.address.country }}</label>
                      </b-field>
                    </div>
                  </div>
                  <div class="columns">
                    <div class="column is-half">
                      <b-field :label="$t('user-details-view.user-address-postcode-field')">
                        <b-input v-model="userData.address.postcode" tabindex="24" v-if="isEditMode"
                                 :placeholder="$t('user-details-view.user-address-postcode-field')"></b-input>
                        <label v-if="!isEditMode">{{ userData.address.postcode }}</label>
                      </b-field>
                    </div>
                  </div>
                  <div class="columns">
                    <ImagePicker v-bind:is-edit-mode="this.isEditMode" v-bind:image-url="this.profilePicUrl"
                                 @onImageFileRemoveSelected="onProfilePicFileRemoveSelected"
                                 @onImageFileSelected="onProfilePicFileSelected" style="margin-left: 0"/>
                  </div>
                </div>
              </div>
              <div class="columns is-mobile" style="margin-top: 16px" v-if="isEditMode">
                <div class="column is-narrow">
                  <b-button class="button" @click="cancelClicked" tabindex="32" icon-left="close-thick">
                    {{ $t('common.cancel') }}
                  </b-button>
                </div>
                <div class="column is-narrow">
                  <b-button class="button is-primary" @click="saveClicked" tabindex="33" icon-left="content-save">
                    {{ $t('common.save') }}
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import firebase from "firebase/app";
import ImagePicker from "@/components/ImagePicker";

export default {
  name: "UserDetails",
  components: {ImagePicker},
  props: ['id'],
  data() {
    return {
      userData: {
        name: {},
        address: {}
      },
      isLoading: false,
      isEditMode: false,
      currentUserRole: null,
      profilePicFile: null,
      profilePicUrl: null
    }
  },
  watch: {
    id: function () {
      console.log("prop id changed.")
      this.readUserData();
      this.isEditMode = false;
    }
  },
  methods: {
    readUserData: function () {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const getUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      getUserFunction({action: "get_user_by_id", document_id: this.userId})
          .then((result) => {
            this.isLoading = false
            console.log("result: ", result.data);
            this.userData = result.data["users"];
          })
          .catch((error) => {
            this.isLoading = false;
            if (error.message === "InvalidPath") {
              this.$router.replace("../users");
            }
            this.showError(error.message)
            console.log("Error getting user: ", error.message);
          });
    },
    updateUser: function (updateData, updateIsArchive) {
      this.isLoading = true
      const functions = firebase.app().functions("europe-west3");
      const updateUserFunction = functions.httpsCallable(this.getSubmitEndpoint());
      updateData["action"] = "update_user";
      updateUserFunction(updateData).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("result: ", result.data);
        this.showSuccess(this.$t('messages.user-updated'));
        if (updateIsArchive === true && result.data.payload !== undefined) {
          this.userData.is_archived = result.data.payload["users"].updated[0].is_archived["current"]
        }
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error updating user: ", error);
      });
      if (!updateIsArchive) {
        this.updateProfilePicture();
      }
    },
    downloadProfilePicUrl: function () {
      this.profilePicFile = null
      this.profilePicUrl = null
      this.getProfilePicRef().getDownloadURL().then((downloadURL) => {
        console.log('ProfilePic available at', downloadURL);
        this.profilePicUrl = downloadURL
      }).catch((error) => {
        console.log('ProfilePic error: ', error.message);
      });
    },
    updateProfilePicture: function () {
      if (this.profilePicFile !== null && this.profilePicFile !== undefined) {
        this.profilePicUrl = ""
        this.getProfilePicRef().put(this.profilePicFile).then((snapshot) => {
          console.log('Profile pic is uploaded: ', snapshot.metadata);
          this.downloadProfilePicUrl()
        }).catch((error) => {
          this.showError(error.message)
          console.log('ProfilePic upload error: ', error.message);
          this.downloadProfilePicUrl()
        });
      } else {
        console.log("No profile pic is selected!")
      }
    },
    getProfilePicRef: function () {
      const imagePath = this.getStorageRootFolder() + '/users/' + this.userId + '/profilePic'
      console.log("image ref: ", imagePath)
      return firebase.storage().ref(imagePath)
    },
    onProfilePicFileSelected: function (selectedImageFile) {
      this.profilePicFile = selectedImageFile
      this.profilePicUrl = URL.createObjectURL(this.profilePicFile)
      console.log("Selected file: ", this.profilePicUrl)
    },
    onProfilePicFileRemoveSelected() {
      this.downloadProfilePicUrl()
    },
    archiveUser: function () {
      const dataFieldsToUpdate = {
        document_id: this.userId,
        is_archived: true
      }
      this.updateUser(dataFieldsToUpdate, true);
    },
    deleteUser: function () {
      this.isLoading = true;
      const functions = firebase.app().functions("europe-west3");
      const deleteProjectFunction = functions.httpsCallable(this.getSubmitEndpoint());
      deleteProjectFunction({
        action: "delete_user",
        document_id: this.id,
      }).then((result) => {
        this.isLoading = false;
        this.isEditMode = false;
        console.log("delete user result: ", result.data);
        this.showSuccess(this.$t('messages.user-deleted'));
        this.$emit('userDeleted');
        this.$router.back();
      }).catch((error) => {
        this.isLoading = false;
        this.showError(error.message)
        console.log("Error getting delete result: ", error);
      });
    },
    /*
     UI Events
     */
    editClicked: function () {
      this.isEditMode = !this.isEditMode;
    },
    deleteClicked: function () {
      if (!this.userData.is_archived) {
        this.$buefy.dialog.alert({
          title: this.$t('user-details-view.delete-confirm-title').toString(),
          message: this.$t('user-details-view.delete-unarchived-user-message').toString(),
          type: 'is-warning',
          hasIcon: true,
          icon: 'alert',
        });
      } else {
        this.$buefy.dialog.confirm({
          title: this.$t('user-details-view.delete-confirm-title').toString(),
          message: this.$t('user-details-view.delete-confirm-message').toString(),
          confirmText: this.$t('user-details-view.delete-confirm-button').toString(),
          cancelText: this.$t('common.cancel').toString(),
          type: 'is-danger',
          hasIcon: true,
          icon: 'delete',
          onConfirm: () => {
            this.deleteUser();
          }
        });
      }
    },
    cancelClicked: function () {
      if (this.isEditMode) {
        this.downloadProfilePicUrl()
        this.readUserData();
        this.isEditMode = !this.isEditMode;
      }
    },
    saveClicked: function () {
      const dataFieldsToUpdate = {
        document_id: this.userId,
        address: this.userData.address,
        name: this.userData.name,
        telephone: this.userData.telephone,
        mobile: this.userData.mobile,
        roles: this.userData.roles
      }
      this.updateUser(dataFieldsToUpdate, false);
    },
    archiveClicked: function () {
      this.$buefy.dialog.confirm({
        title: this.$t('user-details-view.archive-user-confirm-title').toString(),
        message: this.$t('user-details-view.archive-user-confirm-message').toString(),
        confirmText: this.$t('user-details-view.archive-user-confirm-button').toString(),
        type: 'is-danger',
        hasIcon: true,
        icon: 'archive-arrow-down',
        onConfirm: () => this.archiveUser()
      });
    },
    unArchiveClicked: function () {
      const dataFieldsToUpdate = {
        document_id: this.userId,
        is_archived: false
      }
      this.updateUser(dataFieldsToUpdate);
    },
    passwordResetClicked: function () {
      this.isLoading = true
      firebase
          .auth()
          .sendPasswordResetEmail(this.userData.email)
          .then(() => {
            this.isLoading = false
            this.showSuccess(this.$t("password-reset-view.password-reset-sent"))
          })
          .catch((error) => {
            this.isLoading = false
            this.showError(error.message);
          });
    },
    backClicked: function () {
      this.$router.back()
    }
  },
  mounted() {
    if (!this.currentUserAccess("UserDetails", this.UserActions.VIEW, null, this.id)) {
      console.log("Page access denied.");
      this.$router.replace("/projects/").catch(() => {
      });
    }
    this.readUserData();
    this.downloadProfilePicUrl();
    this.currentUserRole = this.getLocalUserRole();
  },
  computed: {
    pageTitle() {
      if (this.isEditMode) {
        return this.$t('user-details-view.edit-user-title')
      } else {
        return this.$t('user-details-view.view-user-title')
      }
    },
    userId() {
      return this.id
    },
    isUserArchived() {
      return this.userData.is_archived
    },
    isCurrentUserSelected() {
      return this.isCurrentUser(this.id);
    },
    isPasswordResetVisible() {
      return (this.currentUserRole !== null && this.currentUserRole !== undefined && this.currentUserAccess("UserDetails", this.UserActions.UPDATE, null, this.id))
          || this.isCurrentUserSelected
    },
    userRole() {
      if (this.userData.roles !== undefined && this.userData.roles.length > 0) {
        const st = this.userRoleOptions.find(obj => {
          return obj.id === this.userData.roles[0]
        });
        if (st !== undefined)
          return this.$t(st.translationKey);
      }
      return this.$t("enums.user-role.inactive");
    }
  }
}
</script>

<style scoped>
.no-margin {
  margin-bottom: 0 !important;
}
</style>
